// ============= Utils =============

import {
  Action as WhatToDoAction,
  Feature as WhatToDoFeature,
  type ResultOption,
} from '@/apis/core/search/whatToDo/post/postWhatToDo';
import {
  FiBriefcase,
  FiFileText,
  FiLayers,
  FiMessageCircle,
} from 'react-icons/fi';
import type { LandingPageHistoryItem, Tutorial } from './Landingpage.types';
import { QUESTION_ASSISTANT_MAX_HISTORY_ITEMS } from './Landingpage.constants';

export const normalizeError = (
  err: unknown,
  fallbackMessage: string,
): Error => (err instanceof Error ? err : new Error(fallbackMessage));

export const getResultOptionsFromResponse = (
  response: unknown,
): ResultOption[] => {
  if (response == null || typeof response !== 'object') return [];

  const outer = response as {
    data?: unknown;
    resultOptions?: unknown;
  };

  if (Array.isArray(outer.resultOptions)) {
    return outer.resultOptions as ResultOption[];
  }

  if (outer.data && typeof outer.data === 'object') {
    const dataObj = outer.data as { resultOptions?: unknown };
    if (Array.isArray(dataObj.resultOptions)) {
      return dataObj.resultOptions as ResultOption[];
    }
  }

  if (Array.isArray(outer.data)) {
    return outer.data as ResultOption[];
  }

  return [];
};

export const isLandingPageHistoryItem = (
  value: unknown,
): value is LandingPageHistoryItem =>
  value != null &&
  typeof value === 'object' &&
  typeof (value as LandingPageHistoryItem).id === 'string' &&
  typeof (value as LandingPageHistoryItem).question === 'string' &&
  typeof (value as LandingPageHistoryItem).answer === 'string' &&
  typeof (value as LandingPageHistoryItem).timestamp === 'number';

export const parseHistoryFromStorage = (
  raw: string | null,
): LandingPageHistoryItem[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isLandingPageHistoryItem)
      .slice(0, QUESTION_ASSISTANT_MAX_HISTORY_ITEMS);
  } catch {
    return [];
  }
};

export const getFirstGreetingName = (
  displayName?: string | null,
  name?: string | null,
): string => {
  const rawName = displayName || name || '';
  const firstToken = rawName.split(' ').filter(Boolean)[1];
  return firstToken || 'there';
};

export const buildGuideCardsFromTutorials = (tutorials: Tutorial[]) =>
  tutorials.map((tutorial) => {
    const normalizedKeywords = (tutorial.keywords || [])
      .join(' ')
      .toLowerCase();
    const normalizedTitle = tutorial.title.toLowerCase();
    const marker = `${normalizedTitle} ${normalizedKeywords}`;

    const Icon =
      marker.includes('ask') || marker.includes('chat')
        ? FiMessageCircle
        : marker.includes('document') || marker.includes('upload')
          ? FiFileText
          : marker.includes('workspace')
            ? FiBriefcase
            : FiLayers;

    return {
      id: tutorial.id,
      title: tutorial.title,
      description: tutorial.description,
      estimatedTime: tutorial.estimatedTime,
      difficulty: tutorial.difficulty,
      stepCount: tutorial.steps?.length ?? 0,
      Icon,
    };
  });

export type ActionPillVariant =
  | 'ask-open'
  | 'ask-create'
  | 'action-open'
  | 'action-create';

export const getActionPillVariant = (
  feature: string,
  action: string,
): ActionPillVariant => {
  const isAsk = feature === WhatToDoFeature.ASK;
  if (isAsk && action === WhatToDoAction.OPEN) return 'ask-open';
  if (isAsk && action === WhatToDoAction.CREATE) return 'ask-create';
  if (action === WhatToDoAction.OPEN) return 'action-open';
  return 'action-create';
};

export type FeaturePillVariant =
  | 'feature-ask'
  | 'feature-collection'
  | 'feature-document'
  | 'feature-persona'
  | 'feature-prompt'
  | 'feature-workflow'
  | 'feature-workspace';

export const getFeaturePillVariant = (feature: string): FeaturePillVariant => {
  switch (feature) {
    case WhatToDoFeature.ASK:
      return 'feature-ask';
    case WhatToDoFeature.COLLECTION:
      return 'feature-collection';
    case WhatToDoFeature.DOCUMENT:
      return 'feature-document';
    case WhatToDoFeature.PERSONA:
      return 'feature-persona';
    case WhatToDoFeature.PROMPT:
      return 'feature-prompt';
    case WhatToDoFeature.WORKFLOWINSTANCE:
      return 'feature-workflow';
    case WhatToDoFeature.WORKSPACE:
      return 'feature-workspace';
    default:
      return 'feature-workspace';
  }
};

export type WorkspacePillVariant =
  | 'workspace-private'
  | 'workspace-collaborative'
  | 'workspace-standard';

export const getWorkspacePillVariant = (
  workspaceType?: string,
): WorkspacePillVariant => {
  if (workspaceType === 'Private') return 'workspace-private';
  if (workspaceType === 'Collaborative') return 'workspace-collaborative';
  return 'workspace-standard';
};
